/**
 * dashboard/api.ts — typed fetch + response shapes for the Manager Metrics Dashboard
 * (REQUIREMENTS_METRICS_DASHBOARD.md). Reuses the generic apiGet/ApiResult helpers from
 * community/api (one normalised result shape across the app): {kind:'ok',body} /
 * {kind:'unauthorized'} (401) / {kind:'error',status,...} (everything else, incl. the 403 the
 * Manager gate uses). These types mirror packages/api/src/db/managerMetrics.ts exactly.
 */
import { apiGet } from '../community/api';
export { type ApiResult, apiGet } from '../community/api';

/**
 * Why a figure is absent (MD-F05) — stated honestly, never faked. Mirrors `FigureAbsence` in
 * `api/src/db/platformActivity.ts`.
 *
 * THIS USED TO BE A TWO-MEMBER `Unavailable` UNION AND NOTHING ELSE, with every Overview figure typed as
 * it — so the page had no `available: true` branch to render and a correct API response could not
 * have been displayed even once the server produced one. Three of the five reasons below are new. Two of
 * them exist because view recording now has a START DATE: a figure whose period reaches back before it
 * is wrong rather than approximate, and one needing more than AE-F18's 90-day retention can never be
 * right at all. The third is cohort suppression, which the engines apply to every people-count they
 * publish — so it is a reason ANY figure on this payload can carry, not a peculiarity of the training
 * ones further down.
 */
export type FigureAbsence =
  | 'awaiting-activity-tracking'
  | 'tracking-too-recent'
  | 'retention-window-too-short'
  | 'headcount-not-recorded'
  | 'too-few';

/** A count of people, or the reason there is not one. */
export type PeopleFigure =
  { available: true; count: number } | { available: false; reason: FigureAbsence };

/**
 * A rate in 0..1 with the two numbers behind it. Both are published only when the figure is available,
 * and the page shows them — a bare percentage invites a reader to infer a population from it.
 */
export type RateFigure =
  | { available: true; rate: number; numerator: number; denominator: number }
  | { available: false; reason: FigureAbsence };

/** MD-F09, over the three periods the requirement names. */
export interface ActivePeople {
  day: PeopleFigure;
  week: PeopleFigure;
  month: PeopleFigure;
}

/** A people-count aggregate that may be cohort-suppressed (< min_cohort_for_aggregate). */
export type CohortCount =
  { available: true; count: number } | { available: false; reason: 'too-few' };

/**
 * The active filter every page echoes back (MD-F01/F02). `teamId` is the resolved team scope every page
 * now echoes (team feature, 0049) — Overview, Tooling and Learning all narrow their people-shaped
 * figures to a team. It is the team's BIGINT id in string form, or null when no team scope applied
 * (none requested, or the caller was not entitled to the one requested).
 */
export interface MetricsFilter {
  windowDays: number;
  departmentId: number | null;
  teamId?: string | null;
}

/**
 * One team the caller may scope the /learning page to (team feature, 0049) — the option list behind
 * the dashboard's team selector. Ids + names only; the dashboard names nobody, so this carries no
 * roster. Mirrors the `{teams}` shape GET /api/manager/metrics/teams returns.
 */
export interface TeamScopeOption {
  teamId: string;
  name: string;
}
export interface TeamScopeResponse {
  teams: TeamScopeOption[];
}

// ── Overview (MD-F07..F16) ────────────────────────────────────────────────────────────────────────

export interface OverviewMetrics {
  windowDays: number;
  /** When view recording began (ISO-8601), or null if it has not. The page states it out loud. */
  trackingSince: string | null;
  reach: RateFigure; // MD-F07
  activePeople: ActivePeople; // MD-F09
  stickiness: RateFigure; // MD-F10
  returnRate: RateFigure; // MD-F11
  dormant: PeopleFigure; // MD-F12
  adoptionConversion: RateFigure; // MD-F22
  adoptionsInWindow: number;
}
/**
 * MD-F04's share of a RECORDED headcount, mirroring `DepartmentAdoptionShare` in
 * packages/api/src/db/managerMetrics.ts:561-563.
 *
 * Like the training figures further down, each marker carries the engine's own `detail` sentence and the
 * view renders THAT rather than mapping the reason to copy of its own. Not because `AwaitingDataCard`
 * would answer the wrong reason — `FigureAbsence` covers both of these now — but because these markers
 * appear in a table CELL, where there is no card to hand a marker to in the first place.
 *
 * `pct` is deliberately NOT capped at 100 server-side, so a share over 100% is how a stale recorded
 * headcount makes itself visible. Print whatever arrives.
 */
export type DepartmentAdoptionShare =
  | { available: true; pct: number; headcount: number }
  | { available: false; reason: 'too-few' | 'headcount-not-recorded'; detail: string };

/**
 * One governed department's adoption position (MD-F17/F02). `adopters` counts distinct Active PEOPLE,
 * never adoption events: /overview also publishes the platform-wide event total
 * `overview.adoptionsInWindow`, and a per-department event count could be subtracted from it to recover
 * the suppressed rows. So no view here may add, total or re-sort these rows — the server sends them in
 * LABEL order precisely so the position of two withheld figures says nothing about which is larger.
 */
export interface DepartmentAdoption {
  departmentId: number;
  label: string;
  adopters: CohortCount;
  share: DepartmentAdoptionShare;
}
export interface AdoptionByDepartment {
  windowDays: number;
  departments: DepartmentAdoption[];
}

/**
 * MD-F36 — movement from the write-once self-declared baseline. Mirrors `CompetencyMovement`
 * (packages/api/src/db/managerMetrics.ts:858-875). Two facts the copy has to carry:
 *
 *  - There is NO denominator and the view must not invent one. `movedUp + movedDown + unchanged` is the
 *    count of declarations, which is the total MD-F06's per-level cells are suppressed against — so
 *    "9 of 18 people moved up" would hand back a withheld cell from another page of this same API.
 *  - It is a POSITION, not a window: the engine takes no `windowDays`, so this figure is byte-identical
 *    for ?period=7, 30 and 180. A tile that sits beside period-filtered ones must say so, or a manager
 *    who moves the selector and sees one figure move will read the other as broken.
 *
 * `movedDown` reports `'too-few'` on a platform where NOBODY has slipped, because 0 is below the cohort
 * floor. The copy must therefore read "too few to report", never "no data": telling "nobody" apart from
 * "two people" is itself the disclosure the floor exists to prevent.
 */
export interface CompetencyMovement {
  movedUp: CohortCount;
  movedDown: CohortCount;
  /** Levels gained across the movedUp cohort; null whenever movedUp is withheld. */
  levelsGained: number | null;
}

/**
 * The daily active-people series behind the Overview line graph (Part B). One point per UTC calendar day
 * across the active window (7/30/180), so the graph changes shape with the period filter. `count` is null
 * for a day whose distinct-active count is below the cohort floor — the line BREAKS there, never plots a
 * zero-that-means-withheld (a genuine no-activity day is a real 0). Days before `trackingSince` are
 * omitted, not backfilled. Mirrors `ActivePeopleSeries` in packages/api/src/db/platformActivity.ts.
 */
export interface ActiveDayPoint {
  day: string; // YYYY-MM-DD (UTC)
  count: number | null; // null = cohort-suppressed
}
export interface ActivePeopleSeries {
  windowDays: number;
  trackingSince: string | null;
  points: ActiveDayPoint[];
}

export interface OverviewResponse {
  asOf: string;
  filter: MetricsFilter;
  overview: OverviewMetrics;
  /** The daily active-people series the line graph plots — replaces the three active-people cards. */
  activePeopleSeries: ActivePeopleSeries;
  /** The two figures the page LEADS on, top-level beside `overview` exactly as the route sends them. */
  adoptionByDepartment: AdoptionByDepartment;
  competencyMovement: CompetencyMovement;
}

// ── Tooling (MD-F17..F28) ─────────────────────────────────────────────────────────────────────────

export interface AdoptionSummary {
  windowDays: number;
  adoptionsInWindow: number;
  adoptionsAllTime: number;
  distinctAdoptedTools: number;
  mostAdopted: { toolId: string; name: string; adoptionCount: number }[];
}
export interface CatalogueGrowth {
  windowDays: number;
  approvedTotal: number;
  newlyPublishedInWindow: number;
}
export interface CreatorsConsumers {
  creators: CohortCount;
  consumers: CohortCount;
  meanToolsPerCreator: number | null;
  distribution: { toolsPublished: number; creatorCount: number }[];
}
export interface EfficiencyEstimate {
  windowDays: number;
  assumedUsesPerMonth: number;
  /**
   * `hourlyRateGbp` and `moneySavedGbpPerMonth` are declared because the server sends them and this
   * mirror's job is to describe the wire — but NOTHING renders them, deliberately. The money headline
   * (MD-F23) is out by product decision, not by omission: an assumed hourly rate multiplied by an
   * assumed uses-per-month is a figure a reader will quote as a saving, and the platform cannot stand
   * behind it. Time saved keeps its tile because its inputs are the builder's own estimates. Do not add
   * a money card back without reopening that decision.
   */
  hourlyRateGbp: number;
  hoursSavedPerMonth: number;
  moneySavedGbpPerMonth: number;
  contributingTools: number;
}
export interface ToolNeedingAttention {
  toolId: string;
  name: string;
  reasons: string[];
}
export interface ReviewQueueHealth {
  windowDays: number;
  pending: number;
  medianWaitDays: number | null;
  longestWaitDays: number | null;
  approvedInWindow: number;
  rejectedInWindow: number;
}
export interface ToolingResponse {
  asOf: string;
  filter: MetricsFilter;
  adoption: AdoptionSummary;
  growth: CatalogueGrowth;
  creatorsConsumers: CreatorsConsumers;
  efficiency: EfficiencyEstimate;
  needsAttention: ToolNeedingAttention[];
  reviewHealth: ReviewQueueHealth;
}

// ── Learning (MD-F29..F37) ────────────────────────────────────────────────────────────────────────

export interface ResourceClicks {
  itemRef: string;
  title: string;
  url: string | null;
  opens: number;
  distinctOpeners: number;
}
export interface NeglectedResource {
  itemRef: string;
  title: string;
  url: string | null;
}
export interface PassRate {
  windowDays: number;
  rate: number | null;
  attempters: CohortCount;
}
export interface CompetencyDistribution {
  suppressed: boolean;
  /**
   * Each cell carries its own CohortCount, not a bare number: MD-F06 suppresses per level, because
   * a three-person cell identifies a small team even when the whole distribution is large enough to
   * publish. `suppressed` remains the whole-distribution gate. No total is sent alongside suppressed
   * cells — that would recover them by subtraction.
   */
  levels: { level: string; count: CohortCount }[];
}
/**
 * MD-F29/F30 — the mandatory-training position. Mirrors the four types in
 * packages/api/src/db/learning.ts:404-442.
 *
 * These carry their OWN unavailable union rather than reusing `FigureAbsence` above, and it is a
 * deliberately NARROWER one: exactly the two reasons a mandatory-training figure can really have, so a
 * view cannot be written against a case that will never arrive. Each marker ships a `detail` sentence
 * written by the engine, so render that instead of mapping the reason to local copy — the engine knows
 * which scope and which cohort floor produced the refusal, and this file would only be guessing at it.
 */
export type TrainingFigureUnavailable =
  | { available: false; reason: 'too-few'; detail: string }
  | { available: false; reason: 'headcount-not-recorded'; detail: string };

/** The state breakdown across the cohort. The three states partition `people` exactly. */
export type TrainingCompletionCounts =
  | {
      available: true;
      people: number;
      complete: number;
      inProgress: number;
      notStarted: number;
      overdue: number; // MD-F30 — a count, never accompanied by identities
    }
  | TrainingFigureUnavailable;

/** MD-F29: people complete ÷ the RECORDED headcount for the scope, as a whole percentage. */
export type TrainingCompletionShare =
  { available: true; pct: number; complete: number; headcount: number } | TrainingFigureUnavailable;

export interface TrainingCompletionSummary {
  departmentId: number | null;
  departmentLabel: string | null;
  /** The governed training_overdue_days the overdue count was measured against. */
  overdueAfterDays: number;
  counts: TrainingCompletionCounts;
  completion: TrainingCompletionShare;
}

export interface LearningResponse {
  asOf: string;
  filter: MetricsFilter;
  /**
   * MD-F37's horizon, which is NOT `filter.windowDays`: the route holds neglect at 90 days whatever the
   * page filter says, because "nobody has opened this in three months" is the question, and re-asking it
   * through a 7-day filter would call almost the whole cache neglected
   * (packages/api/src/http/routes/managerMetrics.ts, NEGLECT_WINDOW_DAYS). It arrives on the payload so
   * the page can STATE it (MD-F24) instead of keeping a matching constant of its own — two copies of one
   * number is how the copy on a page goes quietly false.
   */
  neglectedWindowDays: number;
  topResources: ResourceClicks[];
  neglected: NeglectedResource[];
  passRate: PassRate;
  competency: CompetencyDistribution;
  trainingCompletion: TrainingCompletionSummary;
}

// ── loader plumbing ─────────────────────────────────────────────────────────────────────────────

/**
 * The one loader state every dashboard page returns: signed out, forbidden (not a Manager /
 * PlatformOwner), or ok with the page payload — the same discriminant admin/LearningMetrics.tsx uses.
 */
export type PageState<T> =
  { state: 'signedOut' } | { state: 'forbidden' } | { state: 'ok'; data: T };

/**
 * Carry the URL's `?period` / `?department` through to the API call verbatim. The server whitelists
 * period to 7|30|180 (default 30) and parses department to a positive int or null, so we forward the
 * raw values and let the server be the authority (the response echoes back the filter it applied).
 */
function filterQuery(request: Request): string {
  const url = new URL(request.url);
  const out = new URLSearchParams();
  const period = url.searchParams.get('period');
  const department = url.searchParams.get('department');
  // `teamId` is forwarded verbatim too; the server validates it as all-digits and IGNORES a team the
  // caller may not scope to, so a stale value in the URL falls back to the department/platform scope
  // rather than erroring. All three pages act on it now (team feature, 0049) — each narrows its own
  // people-shaped figures; tool- and department-cross-tab figures ignore it.
  const teamId = url.searchParams.get('teamId');
  if (period) out.set('period', period);
  if (department) out.set('department', department);
  if (teamId) out.set('teamId', teamId);
  const qs = out.toString();
  return qs ? `?${qs}` : '';
}

/** Fetch one dashboard page, mapping 401→signedOut and 403→forbidden (MD-NFR08 in-handler gate). */
async function fetchPage<T>(path: string, request: Request): Promise<PageState<T>> {
  const res = await apiGet<T>(`${path}${filterQuery(request)}`);
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET ${path} failed (${res.status})`);
  }
  return { state: 'ok', data: res.body };
}

// react-router passes { request } to a loader; we only need the request's URL for the filter.
type LoaderArgs = { request: Request };

export function overviewLoader({ request }: LoaderArgs): Promise<PageState<OverviewResponse>> {
  return fetchPage<OverviewResponse>('/api/manager/metrics/overview', request);
}
export function toolingLoader({ request }: LoaderArgs): Promise<PageState<ToolingResponse>> {
  return fetchPage<ToolingResponse>('/api/manager/metrics/tooling', request);
}
export function learningLoader({ request }: LoaderArgs): Promise<PageState<LearningResponse>> {
  return fetchPage<LearningResponse>('/api/manager/metrics/learning', request);
}

/**
 * The teams the caller may scope the dashboard to, for the layout's team selector (team feature,
 * 0049). Non-fatal like the department refdata: a failed or forbidden read simply yields no options,
 * and the selector is then not rendered — a manager with no team, or a viewer who is neither manager
 * nor owner, has nothing to pick. Never throws, so it can be fetched alongside the department list in
 * the layout loader without turning a metrics outage into a broken shell.
 */
export async function fetchTeamScopeOptions(): Promise<TeamScopeOption[]> {
  const res = await apiGet<TeamScopeResponse>('/api/manager/metrics/teams');
  return res.kind === 'ok' ? res.body.teams : [];
}
